import React from "react";
import { cls } from "cls";
import MediaReviewThumbnail from "../MediaReviewThumbnail";
import { getShortenedReviewerName } from "../../CustomerReviewHelper";
import { User } from "~/common/Store/models/User";
import Stars from "~/common/Stars";
import { MediaIdentifier, ProductReviewDetail } from "../../reviewModels";
import HelpfulReviewButtons from "../HelpfulReviewButtons";
import TextReviewContent from "../TextReviewContent";
import VerifiedPurchaseBadge from "../VerifiedPurchaseBadge";
import warehouseLocation from "~/common/Store/models/warehouseLocation";
import ReviewVGVariantDetails from "../ReviewVGVariantDetails";

interface Props {
  review: ProductReviewDetail;
  reviewLocation: number;
  user: User;
  productReviewIndex: number;
  verifiedPurchase?: boolean;
  handleSelectMediaElement: (newMedia: MediaIdentifier | undefined) => void;
  addUserUpvote: (reviewId: number, upvoted: boolean) => void;
  userVoted: boolean;
  userUpvoted: boolean;
}

const ProductReview = (props: Props) => {
  const {
    addUserUpvote,
    handleSelectMediaElement,
    productReviewIndex,
    review,
    reviewLocation,
    user,
    userUpvoted,
    userVoted,
    verifiedPurchase,
  } = props;

  const upvote = userUpvoted ? review.upvote + 1 : review.upvote;
  const totalVotes = userVoted ? review.totalVotes + 1 : review.totalVotes;
  const ratingDate = new Date(review.textReview.dateSubmitted);

  return (
    <article
      aria-labelledby={`article-id-${productReviewIndex}`}
      key={review.reviewId}
      className={`product-review media ${cls("mt-4", "xs:rounded", "md:flex")}`}
      id={`review_${review.reviewId}`}
      data-testid="product-review"
    >
      <div
        className={`product-review__bylin ${cls(
          "flex",
          "flex-col",
          "md:flex-none",
          "md:w-1/4",
          "md:mr-8",
        )}`}
      >
        <div className={cls("mb-4", "flex", "items-center", "md:hidden")}>
          {review.oneToFiveStarRating > 0 && (
            <>
              <span className="sr-only">
                {`Rated ${review.oneToFiveStarRating.toString()} out of 5 stars`}
              </span>
              <div className="mr-3">
                <Stars rating={review.oneToFiveStarRating} />
              </div>
            </>
          )}
        </div>
        <footer id={`article-id-${productReviewIndex}`}>
          <span className="sr-only">Review by</span>
          <div>
            <p
              className={cls(
                "mb-0",
                "text-base",
                "leading-4",
                "md:text-lg",
                "md:leading-5",
              )}
            >
              <strong data-testid={`reviewer-name${productReviewIndex}`}>
                {`${getShortenedReviewerName(review.fullName)} `}
              </strong>
              {review.textReview.company.length > 0 && (
                <>
                  from&nbsp;
                  <strong
                    data-testid={`reviewer-company${productReviewIndex}`}
                  >{`${review.textReview.company} `}</strong>
                </>
              )}
            </p>
          </div>
          <div>
            {review.textReview.dateSubmitted.length > 0 && (
              <p
                className={cls(
                  "text-sm",
                  "mb-0",
                  "leading-4",
                  "text-gray-700",
                  "mt-1",
                )}
              >
                Posted on&nbsp;
                <span data-testid={`review-date${productReviewIndex}`}>
                  {ratingDate.toLocaleDateString(undefined, {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })}
                </span>
              </p>
            )}
          </div>
        </footer>

        {verifiedPurchase && <VerifiedPurchaseBadge />}
      </div>

      <div className="md:w-3/4">
        <div className="hidden md:block">
          {review.oneToFiveStarRating > 0 && (
            <>
              <span className="sr-only">
                {`Rated ${review.oneToFiveStarRating.toString()} out of 5 stars`}
              </span>
              <div className="mr-3">
                <Stars rating={review.oneToFiveStarRating} />
              </div>
            </>
          )}
          {review.oneToFiveStarRating === 0 && <p>Not rated</p>}
        </div>

        <ReviewVGVariantDetails
          review={review}
          styles="text-gray-600 text-sm mt-4"
        />
        <TextReviewContent reviewText={review.textReview.reviewText} />
        <div className="thumbs thumbs--bg-cover">
          {review.mediaReviews.map((mediaReview, index) => (
            <MediaReviewThumbnail
              key={`${productReviewIndex}-thumbnail-${index}`}
              productReviewIndex={index}
              mediaReview={mediaReview}
              setSelectedModalMedia={() =>
                handleSelectMediaElement({
                  id: mediaReview.id,
                  type: mediaReview.type,
                })
              }
              containerClasses={cls("mb-5", "mr-4", "mt-1")}
              thumbnailClasses={cls("md:h-16", "md:w-16", "h-12", "w-12")}
              iconClassName={cls("relative", "fill-current", "text-white")}
              totalMediaReviews={review.mediaReviews.length}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-4">
          <p
            className={`product-review__found-this-helpful ${cls(
              "m-0",
              "text-sm",
              "leading-4",
              "text-left",
            )}`}
            data-testid="found-this-helpful-count"
          >
            {totalVotes > 0 && (
              <>
                {`${upvote} `}
                <span className="non-essential">out</span>
                {` of ${totalVotes} found this `}
                <span>review helpful</span>
              </>
            )}
          </p>
          {user.registered && review.userId !== Number(user.index) && (
            <HelpfulReviewButtons
              addUserUpvote={addUserUpvote}
              userVoted={userVoted}
              userUpvoted={userUpvoted}
              reviewId={review.reviewId}
            />
          )}
        </div>
        {review.textReview.comment.length > 0 && (
          <article
            className={`product-review product-review--customer-service ${cls(
              "text-base",
              "bg-blue-50",
              "border-0",
              "border-blue-500",
              "mt-4",
              "rounded-none",
            )} border-l-[3px]`}
          >
            <p
              className={`product-review__text ${cls("m-0", "p-0")}`}
              dangerouslySetInnerHTML={{ __html: review.textReview.comment }}
            />
            <p
              className={`product-review__byline ${cls(
                "font-bold",
                "text-sm",
                "leading-4",
                "m-0",
                "mt-4",
              )}`}
            >
              {reviewLocation === warehouseLocation.LeolaCDC2
                ? "WebstaurantStore "
                : ""}
              Customer Solutions
            </p>
          </article>
        )}
      </div>
    </article>
  );
};

export default ProductReview;
